import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, LuxuryBucket } from '../../services/analyticsService';

interface LuxuryIntentChartProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const LuxuryIntentChart: React.FC<LuxuryIntentChartProps> = ({
  businessIdentifier,
}) => {
  const [data, setData] = useState<LuxuryBucket[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessIdentifier) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getLuxuryIntentDistribution(businessIdentifier);
        setData(result.data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch luxury intent data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier]);

  const getChartOption = () => {
    if (!data || data.length === 0) return {};

    const ranges = data.map((d) => d.range);
    const counts = data.map((d) => d.count);

    // Gradient from light to dark purple
    const colorStops = data.map((_, idx) => {
      const ratio = idx / Math.max(data.length - 1, 1);
      const r = Math.round(214 - ratio * 80);
      const g = Math.round(188 - ratio * 120);
      const b = Math.round(250 - ratio * 50);
      return `rgb(${r}, ${g}, ${b})`;
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const item = data[idx];
          if (!item) return '';
          return `<b>${item.range}</b><br/>Leads: ${item.count}`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: ranges,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#666',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      yAxis: {
        type: 'value',
        name: 'No. of Leads',
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999' },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series: [
        {
          type: 'bar',
          data: counts.map((count, idx) => ({
            value: count,
            itemStyle: {
              color: colorStops[idx],
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barWidth: '60%',
        },
      ],
    };
  };

  const hasData = data && data.length > 0 && data.some(d => d.count > 0);

  return (
    <ChartCard title="Luxury Intent Index Distribution" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '350px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            Coming Soon — Data will appear as new conversations are processed
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default LuxuryIntentChart;
